import { Modal } from '@/components/admin/Modal';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import type { ResponsePromptTreeNode } from '@innermate/shared';
import { ChevronRight, FileText, Folder } from 'lucide-react';
import { useMemo, useState } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  tree: ResponsePromptTreeNode[];
  /** 전체 트리 export — 다운로드 트리거가 부모. */
  onExportAll: () => void | Promise<void>;
  /** 선택 노드 ids 만 export. */
  onExportSelected: (ids: string[]) => void | Promise<void>;
}

/** node.id 기준 BFS 로 자손 ids 수집 (folder 선택 시 propagation). */
function collectDescendantIds(node: ResponsePromptTreeNode): string[] {
  const out: string[] = [];
  const stack: ResponsePromptTreeNode[] = [...node.children];
  while (stack.length > 0) {
    const n = stack.pop();
    if (!n) break;
    out.push(n.id);
    stack.push(...n.children);
  }
  return out;
}

/**
 * 내보내기 모달 — 모드 토글 (전체 / 선택) + 체크박스 트리.
 *  - folder 체크 시 자손 ids 자동 선택 (visual + 서버 둘 다 평탄화 처리)
 *  - 트리에 한 줄도 안 보이면 "선택 없음" 상태
 */
export function ExportModal({ open, onClose, tree, onExportAll, onExportSelected }: Props) {
  const [mode, setMode] = useState<'all' | 'selected'>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState(false);

  // 노드별 자손/조상 ids 캐시 — 체크박스 propagation 에 사용.
  const descendantsByNode = useMemo(() => {
    const m = new Map<string, string[]>();
    const walk = (nodes: ResponsePromptTreeNode[]) => {
      for (const n of nodes) {
        if (n.type === 'folder') m.set(n.id, collectDescendantIds(n));
        if (n.children.length) walk(n.children);
      }
    };
    walk(tree);
    return m;
  }, [tree]);

  const ancestorsByNode = useMemo(() => {
    const m = new Map<string, string[]>();
    const walk = (nodes: ResponsePromptTreeNode[], ancestors: string[]) => {
      for (const n of nodes) {
        m.set(n.id, ancestors);
        if (n.children.length) walk(n.children, [...ancestors, n.id]);
      }
    };
    walk(tree, []);
    return m;
  }, [tree]);

  /**
   * folder 가 체크되지 않았지만 자손 중 일부가 체크된 상태 — indeterminate 표시용.
   * 서버 walk 는 effective set 만 보므로 시각 표현 용도.
   */
  const isIndeterminate = (n: ResponsePromptTreeNode): boolean => {
    if (n.type !== 'folder') return false;
    if (selected.has(n.id)) return false;
    const desc = descendantsByNode.get(n.id) ?? [];
    return desc.some((id) => selected.has(id));
  };

  /**
   * toggle 정책:
   *  - 노드 체크 → 자신 + (folder 면 자손) 추가. 조상은 그대로
   *  - 노드 해제 → 자신 + (folder 면 자손) 제거. 그리고 **조상 folder 도 모두 해제** —
   *    조상이 체크된 상태로 남아있으면 서버 walk 가 자손을 재포함시켜 UX 불일치 발생
   */
  const toggle = (n: ResponsePromptTreeNode) => {
    const next = new Set(selected);
    const isOn = next.has(n.id);
    if (isOn) {
      next.delete(n.id);
      if (n.type === 'folder') {
        for (const id of descendantsByNode.get(n.id) ?? []) next.delete(id);
      }
      // 조상도 해제해야 서버가 자동 재포함하지 않음. 결과: 같은 부모의 다른 형제는 그대로 유지.
      for (const id of ancestorsByNode.get(n.id) ?? []) next.delete(id);
    } else {
      next.add(n.id);
      if (n.type === 'folder') {
        for (const id of descendantsByNode.get(n.id) ?? []) next.add(id);
      }
    }
    setSelected(next);
  };

  const toggleExpand = (id: string) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  const handleExport = async () => {
    setPending(true);
    try {
      if (mode === 'all') {
        await onExportAll();
      } else {
        await onExportSelected([...selected]);
      }
      onClose();
    } finally {
      setPending(false);
    }
  };

  const renderRow = (node: ResponsePromptTreeNode, depth: number) => {
    const isFolder = node.type === 'folder';
    const isOpen = expanded.has(node.id);
    const isSel = selected.has(node.id);
    return (
      <div key={node.id}>
        <div
          className={cn(
            'flex items-center gap-1.5 px-2 py-1 text-sm hover:bg-surface-muted/50',
            isSel && 'bg-accent/10',
          )}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
        >
          {isFolder ? (
            <button
              type="button"
              onClick={() => toggleExpand(node.id)}
              className="shrink-0 p-0.5 rounded hover:bg-foreground/10 cursor-pointer"
              aria-label={isOpen ? '접기' : '펼치기'}
            >
              <ChevronRight
                className={cn(
                  'size-3.5 text-foreground-muted transition-transform',
                  isOpen && 'rotate-90',
                )}
              />
            </button>
          ) : (
            <span className="inline-block w-4 shrink-0" />
          )}
          <input
            ref={(el) => {
              if (el) el.indeterminate = !isSel && isIndeterminate(node);
            }}
            type="checkbox"
            checked={isSel}
            onChange={() => toggle(node)}
            className="size-3.5 shrink-0 cursor-pointer"
            aria-label={`${node.name} 선택`}
          />
          {isFolder ? (
            <Folder className="size-4 shrink-0 text-foreground-muted" />
          ) : (
            <FileText className="size-3.5 shrink-0 text-foreground-muted" />
          )}
          <span className="truncate">{node.name}</span>
          {!isFolder && node.slug ? (
            <code className="text-[10px] text-foreground-muted font-mono ml-1">{node.slug}</code>
          ) : null}
        </div>
        {isFolder && isOpen && node.children.length > 0 ? (
          <div>{node.children.map((c) => renderRow(c, depth + 1))}</div>
        ) : null}
      </div>
    );
  };

  const selectedCount = selected.size;
  const canExport = mode === 'all' || selectedCount > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="내보내기"
      description="전체 트리 또는 선택한 노드만 JSON 으로 다운로드합니다."
      size="lg"
      footer={
        <div className="flex justify-between gap-2">
          <span className="text-xs text-foreground-muted">
            {mode === 'selected' ? `선택 ${selectedCount} 건` : '전체 트리'}
          </span>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
              취소
            </Button>
            <Button type="button" onClick={handleExport} disabled={!canExport || pending}>
              {pending ? '내보내기 중…' : '내보내기'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="flex gap-1 rounded-md border border-border p-0.5 w-fit">
          <button
            type="button"
            onClick={() => setMode('all')}
            className={cn(
              'px-3 py-1 text-xs rounded cursor-pointer',
              mode === 'all'
                ? 'bg-accent/15 text-accent font-medium'
                : 'text-foreground-muted hover:bg-surface-muted',
            )}
          >
            전체 트리
          </button>
          <button
            type="button"
            onClick={() => setMode('selected')}
            className={cn(
              'px-3 py-1 text-xs rounded cursor-pointer',
              mode === 'selected'
                ? 'bg-accent/15 text-accent font-medium'
                : 'text-foreground-muted hover:bg-surface-muted',
            )}
          >
            선택 노드
          </button>
        </div>

        {mode === 'all' ? (
          <div className="rounded-md bg-surface-muted/30 px-3 py-2 text-sm text-foreground-muted">
            모든 폴더와 파일을 한 JSON 으로 내보냅니다. variants 와 활성 본문이 포함됩니다 (버전
            히스토리는 제외).
          </div>
        ) : (
          <>
            <div className="text-xs text-foreground-muted">
              folder 체크 시 자손이 자동 선택됩니다. 선택 외 조상은 export 에 포함되지 않으며, 해당
              노드들은 import 시 root 로 들어갑니다.
            </div>
            <div className="max-h-80 overflow-y-auto rounded-md border border-border">
              {tree.length === 0 ? (
                <div className="p-3 text-sm text-foreground-muted text-center">
                  트리가 비어있습니다.
                </div>
              ) : (
                tree.map((n) => renderRow(n, 0))
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
